import {useMutation, UseMutationResult} from "@tanstack/react-query";
import {client} from "@/utils/detectors";
import {formatDeviceInfo} from "@/features/poll/utils/deviceInfo";
import {api} from "@/features/api";
import {getUser} from "@/features/api/lib/firebase";
import {qsetVersionFromPath} from "@/features/poll/utils/qsetRouting";
import {getActiveQsetLog} from "@/features/poll/utils/qsetLogBuffer";
import {
  buildQsetLogPathBlock,
  formatVisualViewport,
  lastQuestionId,
  resolveQsetSlug,
  unavailableBlock,
} from "@/features/poll/utils/qsetLogFeedback";
import {RELEASE} from "@/constants/release";
import Config from "@/config";
import {currentQuestionIdGet} from "@/utils/localStorage";
import {
  readClientHints,
  readPlatformSync,
} from "@/features/poll/utils/clientHints";
import {
  formatQsetJsonLine,
  formatWidgetLines,
  getActiveQset,
  pollSlugFromPath,
} from "@/features/poll/utils/activeQset";
import {
  buildResultsLine,
  findCardVideo,
  formatConnectivityLines,
  formatLogIdsLine,
  formatQuestionProgressLines,
  formatVideoLine,
  type RtdbNode,
} from "@/features/poll/utils/feedbackContext";
import {formatVideoLogLines} from "@/features/poll/utils/videoLogSummary";
import {readConnection} from "@/features/poll/utils/resultClient";
import {readConsoleErrors} from "@/features/poll/utils/consoleErrorBuffer";
import {formatConsoleErrorsBlock} from "@/features/poll/utils/consoleErrorBlock";
import type {QueuedLogRecord} from "@apps/qset-log-batcher/policy";

/**
 * Resolves the "Qset log:" line for the feedback report: the RTDB node the
 * log lives in, never its content (#1249).
 *
 * A live batcher on this page is flushed first, so the node holds the events
 * still queued when the report is sent; its local snapshot supplies the last
 * question id without anything leaving the device. With no live batcher the
 * node is still named -- the slug from the URL and the signed-in user are
 * all it takes -- but nothing is read back. Any failure degrades to an
 * `unavailable (...)` line; it must never stop the feedback POST itself.
 *
 * Also returns the node itself, for the `Qset result:` line, and the snapshot,
 * for the position and time on the question.
 */
interface QsetLogResolution {
  block: string;
  questionId: string | undefined;
  node?: RtdbNode;
  records: ReadonlyArray<QueuedLogRecord>;
}

const resolveQsetLogBlock = async (
  user: {uid?: string} | null | undefined,
): Promise<QsetLogResolution> => {
  try {
    const active = getActiveQsetLog();
    if (active) {
      const flushFailed = !(await active.batcher.flush("feedback"));
      const records = active.batcher.snapshot();
      return {
        block: buildQsetLogPathBlock({
          dbUrl: Config.RTDB_API_URL,
          slug: active.pollSlug,
          userId: active.userId,
          flushFailed,
        }),
        questionId: lastQuestionId(records),
        node: {slug: active.pollSlug, userId: active.userId},
        records,
      };
    }

    const slug = resolveQsetSlug(window.location.pathname);
    if (!slug) {
      return {
        block: unavailableBlock("no-slug"),
        questionId: undefined,
        records: [],
      };
    }
    if (!user?.uid) {
      return {
        block: unavailableBlock("signed-out"),
        questionId: undefined,
        records: [],
      };
    }
    return {
      block: buildQsetLogPathBlock({
        dbUrl: Config.RTDB_API_URL,
        slug,
        userId: user.uid,
      }),
      questionId: undefined,
      node: {slug, userId: user.uid},
      records: [],
    };
  } catch {
    return {
      block: unavailableBlock("error"),
      questionId: undefined,
      records: [],
    };
  }
};

/**
 * How long the report waits for the high-entropy client hints. They resolved
 * late on every zoo run (28 of 28, after the first paint), and a report must
 * never stall on a promise the browser may not settle at all.
 */
export const CLIENT_HINTS_TIMEOUT_MS = 1000;

const readTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
  } catch {
    return "unknown";
  }
};

/**
 * The team reads reports in Prague time, whatever the reporter's own zone;
 * `sv-SE` is the locale that formats as `YYYY-MM-DD HH:mm:ss`. Built per
 * report, not at import: `shortOffset` is a RangeError before Chrome 95 /
 * Safari 15.4, and this module loads with the app shell.
 */
const formatPragueTime = (now: Date): string => {
  try {
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Europe/Prague",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "shortOffset",
    }).format(now);
  } catch {
    return "unavailable";
  }
};

const formatSentAt = (now: Date): string =>
  `${now.toISOString()} (Europe/Prague: ${formatPragueTime(now)}; reporter time zone: ${readTimeZone()})`;

/**
 * Reading a media element or the DOM must never stop the report; a throw
 * leaves the line out.
 */
const readVideoLine = (
  questionId: string | null | undefined,
): string | undefined => {
  try {
    return formatVideoLine(findCardVideo(document, questionId));
  } catch {
    return undefined;
  }
};

/**
 * `client` is a snapshot taken when the app loaded; sizes and the connection
 * are read again here, at send time, since they are what the reporter had on
 * screen when something went wrong.
 */
const readDeviceInfo = (): string => {
  try {
    return formatDeviceInfo({
      userAgent: client.userAgent,
      userAgentData: client.userAgentData,
      screen: {width: window.screen.width, height: window.screen.height},
      window: {width: window.innerWidth, height: window.innerHeight},
      maxTouchPoints: client.maxTouchPoints,
      hardwareConcurrency: client.hardwareConcurrency,
      deviceMemory: client.deviceMemory,
      connection: readConnection(navigator as {connection?: object}),
      languages: client.languages,
      cookieEnabled: client.cookieEnabled,
      doNotTrack: client.doNotTrack,
      browser: client.browser,
      device: client.device,
      bot: client.bot,
    });
  } catch {
    return "unavailable";
  }
};

const readOrientation = (): string | undefined => {
  try {
    if (screen.orientation?.type) return screen.orientation.type;
    return window.matchMedia("(orientation: portrait)").matches
      ? "portrait"
      : "landscape";
  } catch {
    return undefined;
  }
};

/**
 * Platform, OS version and device model, read the way `qset-start` reads them
 * for the log (#1249): the UA string freezes Android at "10; K", so only the
 * client hints carry the real version. Chromium-only; elsewhere every line is
 * `unavailable` and the UA in Device info is all there is. The low-entropy
 * platform is read synchronously, so it survives a hints read that times out.
 */
const readPlatformLines = async (): Promise<string> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), CLIENT_HINTS_TIMEOUT_MS);
  });
  const hints = await Promise.race([
    readClientHints().catch(() => undefined),
    timeout,
  ]);
  clearTimeout(timer);

  const platform = hints?.platform || readPlatformSync() || "unavailable";
  const platformVersion = hints?.platformVersion || "unavailable";
  const model = hints?.model || "unavailable";
  return `Platform: ${platform}\nPlatform version: ${platformVersion}\nModel: ${model}`;
};

/** Last in the report, and the only part of it that is ever cut (#1285). */
const readConsoleErrorsBlock = (): string => {
  try {
    return formatConsoleErrorsBlock(readConsoleErrors());
  } catch {
    return "Console errors: unavailable (error)";
  }
};

const usePostUserFeedback: () => UseMutationResult = () =>
  useMutation({
    mutationFn: async (message) => {
      const deviceInfo = readDeviceInfo();
      const pollSlug = pollSlugFromPath(window.location.pathname);
      // Started first: it runs alongside the log flush instead of after it.
      const platformLinesPromise = readPlatformLines();
      const user = await getUser();

      const {
        block: qsetLogBlock,
        questionId: qsetLogQuestionId,
        node: qsetLogNode,
        records: qsetLogRecords,
      } = await resolveQsetLogBlock(user);
      // The id MinuteQset reported for the card on stage (#1287) wins, as on
      // v3; the live batcher's last question-enter is only a fallback.
      const currentQuestionId = currentQuestionIdGet() ?? qsetLogQuestionId;

      // Which arm of the v3/v4 split the reporter was on. Read from the URL
      // rather than the build flag, so a poll a v4 build serves from `/poll1`
      // (see resolvePollBasePath) is reported as v3.
      const qsetVersion =
        qsetVersionFromPath(window.location.pathname) ?? "unknown";

      const visualViewport = formatVisualViewport(window.visualViewport);
      const platformLines = await platformLinesPromise;
      const activeQset = getActiveQset();
      const widgetLines = formatWidgetLines(
        activeQset,
        currentQuestionId,
        pollSlug,
      );
      const progressLines = formatQuestionProgressLines({
        records: qsetLogRecords,
        entry: activeQset,
        questionId: currentQuestionId,
        pollSlug,
        now: Date.now(),
      });
      const videoLine = readVideoLine(currentQuestionId);
      const videoLogLines = formatVideoLogLines(
        qsetLogRecords,
        currentQuestionId,
        Date.now(),
      );
      const connectivityLines = formatConnectivityLines({
        orientation: readOrientation(),
        online: navigator.onLine,
      });
      const resultsLine = buildResultsLine(Config.RTDB_API_URL, qsetLogNode);
      const logIdsLine = formatLogIdsLine(qsetLogRecords, currentQuestionId);
      const qsetJsonLine = formatQsetJsonLine(
        activeQset,
        pollSlug,
        Config.CDN_URL,
      );

      // UTC first, so it lines up with `serverTimestamp` in `results-logs`
      // without guessing the reporter's offset; the reporter's zone is there
      // to read their own "it froze at five" against it. Taken last, as close
      // to the POST as possible.
      const sentAt = formatSentAt(new Date());
      const consoleErrorsBlock = readConsoleErrorsBlock();

      const videoBlock = [videoLine, videoLogLines].filter(Boolean).join("\n");

      const text =
        `${message}\n\nSent at: ${sentAt}\n` +
        `User ID: ${user?.uid ?? "unavailable (signed out)"}\n` +
        `Poll slug: ${pollSlug ?? "unavailable (not a poll page)"}\n` +
        `Question ID: ${currentQuestionId ?? "unavailable (no question on stage)"}\n` +
        `${progressLines}\n\n` +
        `Qset version: ${qsetVersion}\nRelease: ${RELEASE ?? "unknown"}\n\n` +
        (videoBlock ? `${videoBlock}\n\n` : "") +
        `Poll: ${window.location.href}\n${qsetJsonLine}\n` +
        `${resultsLine}\n${qsetLogBlock}\n${logIdsLine}\n\n` +
        `${widgetLines}\n\n` +
        `Device info:\n${platformLines}\n${connectivityLines}\n` +
        `Visual viewport: ${visualViewport}\n${deviceInfo}\n\n` +
        consoleErrorsBlock;

      const response = await api("TRENDARO_API_URL").post(
        `/users/me/feedback`,
        {
          json: {message: text},
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} při odesílání zpětné vazby.`);
      }

      return true;
    },
  });

export default usePostUserFeedback;
